import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid"
import {
  Alert,
  AlertTitle,
  Button,
  Switch,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import DeleteIcon from "@mui/icons-material/Delete"
import axios from "axios"
import { fetchToken } from "../../../../Auth"
import { adminURL } from "../../../../global"
import Backdrop from '@mui/material/Backdrop';
function LearningMaterialManagement({}) {
  const navigate = useNavigate()
  const location = useLocation()

  // Simplified privileges handling
  const [privileges, setPrivileges] = useState({})

  // State variables
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [materials, setMaterials] = useState([])
  const [columns, setColumns] = useState([])
  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [materialToDelete, setMaterialToDelete] = useState(null)

  // Navigation handlers
  const goToCreatePage = () =>
    navigate(`/niyantran-panel/${adminURL}/hr-content/learning-material-management/create`, {
      state: { privileges },
    })

  const goToEditPage = (pageURL) =>
    navigate(`/niyantran-panel/${adminURL}/hr-content/learning-material-management/edit/${pageURL}`, {
      state: { privileges },
    })
    const getPrivileges = async () => {
      try {
        const response = await axios.get('/get_privileges', {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        });
    
        if (response.status === 200) {
          // If response.data is undefined, set all privileges to false
          if (response.data === undefined) {
            setPrivileges({
              superAdmin: false,
              createPrivilege: false,
              livePrivilege: false,
              editPrivilege: false,
              deletePrivilege: false,
            });
          } else {
            // If response.data exists, set privileges based on the data
            setPrivileges({
              superAdmin: Boolean(response.data["superAdmin"]),
              createPrivilege: Boolean(response.data["7"]["create"]),
              livePrivilege: Boolean(response.data["7"]["makeLive"]),
              editPrivilege: Boolean(response.data["7"]["edit"]),
              deletePrivilege: Boolean(response.data["7"]["delete"]),
            });
          }
          // setFetch(true);
        }
      } catch (err) {
        console.error("Error", err);
        if (err.response?.status === 401) {
          navigate(`/niyantran-panel/${adminURL}/login`);
        }
      }
    };
  useEffect(() => {
    getPrivileges()

  }, [])
  // Delete handlers
  const openDeleteDialog = (id) => {
    setMaterialToDelete(id)
    setShowDeleteDialog(true)
  }

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false)
    setMaterialToDelete(null)
  }

  const deleteMaterial = async () => {
    if (!materialToDelete) return

    try {
      await axios.post(
        "/backend/delete_learning_material",
        { id: materialToDelete },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )

      // Remove the deleted item from the state
      setMaterials(materials.filter((item) => item.unique_id !== materialToDelete))
      closeDeleteDialog()
      alert("Learning material deleted successfully!")
    } catch (error) {
      console.error("Error deleting material:", error)
      alert("Error deleting learning material. Please try again.")
      closeDeleteDialog()
    }
  }

  // Toggle active state
  const toggleActiveState = async (isCurrentlyActive, materialId) => {
    try {
      // Flag: 1 for publish (true), 0 for draft (false)
      const flag = isCurrentlyActive ? 0 : 1

      const response = await axios.post(
        "/backend/change_learning_material_status",
        {
          id: materialId,
          flag: flag,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )

      // Update state based on response
      if (response.data) {
        fetchMaterials()
      }
    } catch (error) {
      console.error("Error updating state:", error)
      alert("Failed to update status. Please try again.")
    }
  }

  // Fetch learning materials
  const fetchMaterials = async () => {
    try {
      setIsLoading(true)
      setErrorMessage(null)

      const response = await axios.get(`/backend/get_learning_material/0`, {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      // Process API data
      const data = response.data.map((item, index) => ({
        id: index + 1,
        unique_id: item.id,
        title: item.title || "Untitled",
        state: item.isLive ? "publish" : "draft", // Convert boolean to string state
        publish_date: item.created ? item.created.split(" ")[0] : "",
        pageURL: item.pageURL || "",
        isActive: item.isLive, // Use isLive directly for isActive
      }))

      setMaterials(data)
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching materials:", error)
      setErrorMessage("Failed to load learning materials. Please try again.")
      setIsLoading(false)
    }
  }

  // Setup columns for DataGrid
  const setupColumns = () => {
    const baseColumns = [
      {
        field: "id",
        headerName: "Sr. no.",
        type: "number",
        align: "center",
        flex: 0.9,
        headerClassName: "bg-grey-3 font-bold",
      },
      {
        field: "title",
        headerName: "Title",
        type: "string",
        flex: 2,
        headerClassName: "bg-grey-3 font-bold",
      },
      {
        field: "state",
        headerName: "State",
        type: "string",
        flex: 1,
        headerClassName: "bg-grey-3 font-bold",
        valueGetter: (params) => params.value.charAt(0).toUpperCase() + params.value.slice(1),
      },
      {
        field: "publish_date",
        headerName: "Created Date",
        type: "string",
        flex: 1.3,
        headerClassName: "bg-grey-3 font-bold",
        valueGetter: (params) => {
          if (params.value === "") return ""
          const parts = params.value.split("-")
          return `${parts[2]}-${parts[1]}-${parts[0]}`
        },
      },
      {
        field: "pageURL",
        headerName: "Page URL",
        type: "string",
        flex: 1,
        headerClassName: "bg-grey-3 font-bold",
      },
    ]

    const actionColumns = []

    // Add Active toggle if user has makeLive privilege
    if (privileges.livePrivilege) {
      actionColumns.push({
        field: "isActive",
        headerName: "Active",
        filterable: false,
        flex: 1,
        headerClassName: "bg-grey-3 font-bold",
        renderCell: (params) => (
          <Switch
            checked={params.row.isActive}
            onChange={() => toggleActiveState(params.row.isActive, params.row.unique_id)}
            inputProps={{ "aria-label": "controlled" }}
          />
        ),
      })
    }

    // Add Edit button if user has edit privilege
    if (privileges.editPrivilege) {
      actionColumns.push({
        field: "edit",
        type: "actions",
        headerName: "Edit",
        flex: 1,
        headerClassName: "bg-grey-3 font-bold",
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => goToEditPage(params.row.pageURL)}
          />,
        ],
      })
    }

    // Add Delete button if user has delete privilege
    if (privileges.deletePrivilege) {
      actionColumns.push({
        field: "delete",
        type: "actions",
        headerName: "Delete",
        flex: 1,
        headerClassName: "bg-grey-3 font-bold",
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => openDeleteDialog(params.row.unique_id)}
            sx={{ color: "#ff3333" }}
          />,
        ],
      })
    }

    // Add View URL button
    actionColumns.push({
      field: "view",
      type: "actions",
      headerName: "URL",
      flex: 1,
      headerClassName: "bg-grey-3 font-bold",
      getActions: (params) => {
        if (params.row.isActive) {
          return [
            <GridActionsCellItem
              key="view"
              icon={<OpenInNewIcon />}
              label="View"
              onClick={() => window.open(`/learning-materials/${params.row.pageURL}`, "_blank")}
            />,
          ]
        } else {
          return []
        }
      },
    })

    setColumns([...baseColumns, ...actionColumns])
  }

  // Initialize privileges from props or location state

  // Setup columns and fetch data when privileges change
  useEffect(() => {
    setupColumns()
  }, [privileges])

  // Initial data fetch
  useEffect(() => {
    fetchMaterials()
  }, [])

  // Helper function to display user privileges
  // const getPrivilegesText = () => {
  //   const { createPrivilege, livePrivilege, editPrivilege, deletePrivilege } = privileges

  //   if (!createPrivilege && !editPrivilege && !livePrivilege && !deletePrivilege) {
  //     return "You do not have any privileges for managing Learning Materials."
  //   }

  //   const privilegesList = []
  //   if (createPrivilege) privilegesList.push("create")
  //   if (livePrivilege) privilegesList.push("make live")
  //   if (editPrivilege) privilegesList.push("edit")
  //   if (deletePrivilege) privilegesList.push("delete")

  //   // Format the list with proper grammar
  //   let text = "You have "
  //   if (privilegesList.length === 1) {
  //     text += privilegesList[0]
  //   } else if (privilegesList.length === 2) {
  //     text += `${privilegesList[0]} and ${privilegesList[1]}`
  //   } else {
  //     const lastPrivilege = privilegesList.pop()
  //     text += `${privilegesList.join(", ")}, and ${lastPrivilege}`
  //   }

  //   text += " access for Learning Materials"
  //   return text
  // }

  return (

    <>
    <Box className="w-full p-4">
      {/* Header Card */}
      <Card className="shadow-md rounded-lg w-full mb-4">
        <CardContent className="p-6 flex flex-col md:flex-row justify-between items-center">
          <Box className="md:w-8/12">
            <Typography variant="h4" component="h1" className="font-bold">
              Learning Materials Admin
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and Manage Learning Materials
            </Typography>
          </Box>
          {isLoading ? (
  <Backdrop
    open={isLoading}
    sx={(theme) => ({ color: 'rgb(255, 197, 11)', zIndex: theme.zIndex.drawer + 1 })}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
) : (
  <Box className="md:w-4/12 flex justify-center mt-4 md:mt-0">
 {privileges.createPrivilege && (
  <Button variant="contained" size="large" onClick={goToCreatePage}>
    Create New Material
  </Button>
)}
  </Box>
)}
        </CardContent>
      </Card>

      {/* Main Content Card */}
      <Card className="shadow-md rounded-lg w-full p-4">
        {/* Data Grid or Loading/Error States */}
        {isLoading ? (
          <Box className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Typography className="ml-3">Loading learning materials...</Typography>
          </Box>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : materials.length ? (
          <Box style={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={materials}
              columns={columns}
              sx={{
                "& .MuiDataGrid-menuIcon": {
                  display: "none",
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[5, 10, 25]}
              checkboxSelection={false}
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          <Alert severity="info">No Learning Materials created.</Alert>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this learning material? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={deleteMaterial} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>

  )
}

export default LearningMaterialManagement

  